import {
  Page,
  Button,
  useFlashMessages,
  Banner,
  Stack,
  Text,
} from "@towersystems/roam-common-native";
import { StackScreenProps } from "@react-navigation/stack";
import { CreateProductRootParamList } from "../../../../types";
import { useFormContext } from "react-hook-form";
import { Colors } from "react-native-ui-lib";
import React from "react";
import { useMutation } from "@apollo/client";
import { Product } from "@towersystems/roam-common/lib/generated-types";
import {
  useCommonFormActions,
  useProductFormActionsContextProvider,
} from "../../components/ProductForm/hooks";
import { ProductForm } from "../../components/ProductForm";
import { ProductFormValues } from "../../components/ProductForm/types";
import {
  MUTATION_CREATE_PRODUCT,
  MUTATION_GENERATE_BARCODE,
  productFormDataToGraphqlInput,
} from "../../utils";
import { useProductFormBarcodeLookup } from "../../utils/barcode/hooks";

export type CreateProductScreenProps = StackScreenProps<
  CreateProductRootParamList,
  "CreateProductScreen"
>;

export const CreateProductScreen = ({
  navigation,
}: CreateProductScreenProps) => {
  const { onAfterAddProduct } = useProductFormActionsContextProvider() ?? {};
  const { existingProductId } = useCommonFormActions();
  const { handleSubmit, formState, setError, watch } =
    useFormContext<ProductFormValues>();

  const { loading, handleOnBarcodeLookup } = useProductFormBarcodeLookup();

  const [m] = useMutation<{ createProduct: Product }>(MUTATION_CREATE_PRODUCT);

  const flashMessagesContext = useFlashMessages();

  const { isDirty, isLoading, isSubmitting, isValid, isSubmitSuccessful } =
    formState;

  const watchedStockNumber = watch("stockNumber");
  const watchedProductName = watch("name");

  const [generateBarcodeMutation] = useMutation<{
    generateProductBarcode: string;
  }>(MUTATION_GENERATE_BARCODE);

  const handleGenerateBarcode = React.useCallback(async () => {
    try {
      const result = await generateBarcodeMutation();
      if (!result.data?.generateProductBarcode) {
        throw new Error("Failed to generate barcode");
      }
      const barcode = result.data.generateProductBarcode;
      return barcode;
    } catch (e: any) {
      const msg = e?.message ?? "Something went wrong";
      flashMessagesContext.showMessage({
        message: msg,
        severity: "error",
      });
    }
  }, []);

  const handleOnSubmit = React.useCallback(
    async (values: ProductFormValues) => {
      try {
        const result = await m({
          variables: {
            input: {
              ...productFormDataToGraphqlInput(values),
            },
          },
        });
        if (!result.data?.createProduct) {
          throw new Error("Failed to create product");
        }
        await onAfterAddProduct?.(result.data.createProduct, navigation);
      } catch (e: any) {
        const msg = e?.message ?? "Something went wrong";
        flashMessagesContext.showMessage({
          message: msg,
          severity: "error",
        });

        setError("root", {
          type: "serverError",
          message: msg,
        });
      }
    },
    [navigation]
  );

  const matchingProductFoundMarkup = existingProductId ? (
    <Banner
      title="Matching Product Found!"
      status="warning"
      onPress={async () => {
        await onAfterAddProduct?.({ id: existingProductId } as any, navigation);
      }}
    >
      <Text>{`Product with stock number ${watchedStockNumber} already exists in system. Click here to add to invoice.`}</Text>
    </Banner>
  ) : undefined;

  const disabled =
    !isDirty ||
    isLoading ||
    isSubmitting ||
    !isValid ||
    isSubmitSuccessful ||
    !!existingProductId;

  const footerMarkup = (
    <Button
      disabled={disabled}
      label="Create Product"
      backgroundColor={Colors.action}
      color="white"
      onPress={handleSubmit(handleOnSubmit)}
    ></Button>
  );

  return (
    <Page title="Create new Product" footer={footerMarkup}>
      <Stack vertical>
        {matchingProductFoundMarkup}
        <ProductForm
          onSelectCategoryPress={() => {
            navigation.navigate("ProductSelectCategoryScreen");
          }}
          onSelectDepartmentPress={() => {
            navigation.navigate("ProductSelectDepartmentScreen");
          }}
          onSelectTaxGroupPress={() => {
            navigation.navigate("ProductSelectTaxGroupScreen");
          }}
          onSelectSupplierPress={() => {
            navigation.navigate("ProductSelectSupplierScreen");
          }}
          onManageTagsPress={() => {
            navigation.navigate("ManageProductTagsRootScreen", {
              screen: "ManageProductTagsScreen",
            });
          }}
          onGenerateBarcodePress={handleGenerateBarcode}
          onSelectUomPress={() => {
            navigation.navigate("ProductSelectUomScreen");
          }}
          onStockNumberFormInputBlur={() =>
            !watchedProductName && handleOnBarcodeLookup()
          }
          onLlmQuickWinButtonPress={() =>
            navigation.navigate("ProductDescriptionLlmQuickWinRootScreen", {
              screen: "GenerateProductDescriptionRootScreen",
              params: {
                screen: "ProductDescriptionLlmQuickWinScreen",
              },
            })
          }
          onTagsLlmQuickWinButtonPress={() => {
            navigation.navigate("ProductTagsLlmQuickWinRootScreen", {
              screen: "ProductTagsReviewScreen",
            });
          }}
          onRetailPricingLlmQuickWinButtonPress={() => {
            navigation.navigate("ProductPricingLlmQuickWinRootScreen", {
              screen: "ProductPricingReviewScreen",
            });
          }}
          onProductNameLlmQuickWinButtonPress={() => {
            navigation.navigate("ProductSeoFriendlyNameLlmQuickWinRootScreen", {
              screen: "ProductSeoFriendlyNameReviewScreen",
            });
          }}
        />
      </Stack>
    </Page>
  );
};
